import { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, CircleNotch as Loader2, Warning } from '@phosphor-icons/react';
import {
  logEnquiry,
  type IntakeArrivalChannel,
  type IntakeInstructingPartyType,
  type IntakeFeeStatus,
  type IntakeFeeBasis,
  type IntakeUrgency,
  type LogEnquiryPayload,
} from '@/lib/intakeApi';
import { getApiErrorMessage } from '@/lib/api';
import { startEnquiryFromConversation } from '@/lib/inboxApi';
import { PhoneInput } from '@/components/ui/phone-input';
import {
  isValidEmail,
  isValidPhone,
  entityNameError,
  sanitizeName,
  EMAIL_ERROR,
  PHONE_ERROR,
} from '@/lib/validators';
import { ScrollLock } from '@/components/shared/ScrollLock';

/** What an email conversation can tell us before a human fills in the rest. */
export interface EnquiryPrefill {
  solicitorEmail?: string;
  solicitorName?: string;
  summary?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onLogged: () => void;
  /**
   * When present the enquiry is raised FROM this mail conversation: it posts to the
   * conversation endpoint instead of the plain intake one, so the server can set provenance
   * and claim the conversation atomically. The arrival channel is then email by definition.
   */
  conversationId?: string;
  /** Seeded from the conversation. Only ever a starting point — a person still confirms it. */
  prefill?: EnquiryPrefill;
}

const CHANNELS: Array<{ value: IntakeArrivalChannel; label: string }> = [
  { value: 'phone', label: 'Telephone' },
  { value: 'email', label: 'Email' },
  { value: 'direct', label: 'Direct to counsel' },
  { value: 'referral', label: 'Referral' },
  { value: 'portal', label: 'Court portal' },
  { value: 'web_form', label: 'Website form' },
  { value: 'internal', label: 'Raised internally' },
];

const PARTY_TYPES: Array<{ value: IntakeInstructingPartyType; label: string; hint: string }> = [
  { value: 'professional_client', label: 'Solicitor / professional client', hint: 'Firm, name and email are required.' },
  { value: 'lay_client', label: 'Lay client (Public Access)', hint: 'No solicitor involved. Public Access rules apply.' },
  { value: 'portal', label: 'Court / legal aid portal', hint: 'Access granted on a court system rather than instructed by email.' },
  { value: 'internal', label: 'Chambers (internal)', hint: 'Raised inside chambers.' },
];

const FEE_STATUSES: Array<{ value: IntakeFeeStatus; label: string }> = [
  { value: 'none', label: 'Not discussed' },
  { value: 'estimated', label: 'Estimate given' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'agreed', label: 'Agreed' },
  { value: 'declined', label: 'Declined on fee' },
];

const FEE_BASES: Array<{ value: IntakeFeeBasis; label: string }> = [
  { value: 'brief_fee', label: 'Brief fee' },
  { value: 'refresher', label: 'Refresher' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'conference', label: 'Conference' },
  { value: 'opinion', label: 'Opinion' },
  { value: 'cfa', label: 'CFA' },
  { value: 'dba', label: 'DBA' },
  { value: 'pro_bono', label: 'Pro bono' },
];

const URGENCIES: Array<{ value: IntakeUrgency; label: string }> = [
  { value: 'routine', label: 'Routine' },
  { value: 'expedited', label: 'Expedited' },
  { value: 'urgent', label: 'Urgent' },
];

const INITIAL_STATUSES: Array<{ value: 'new' | 'reviewing' | 'assessing' | 'quoted'; label: string }> = [
  { value: 'new', label: 'New' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'assessing', label: 'Assessing' },
  { value: 'quoted', label: 'Quoted' },
];

const labelCls = 'block text-xs font-medium text-muted-foreground mb-1 sm:mb-1.5';
const inputCls =
  'block w-full min-w-0 max-w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm sm:py-2 outline-none focus:ring-2 focus:ring-ring';

/**
 * LogEnquiryModal — the door chambers actually receives work through.
 *
 * Until this existed the only way work could enter Jurivoxa was a solicitor filling in the
 * public web form, so the telephone call — the commonest arrival of all, and the whole of a
 * solo barrister's front door — had nowhere to go.
 *
 * Deliberately short. A clerk is on the phone while they type this: only the client and a
 * summary are required, plus the solicitor's details when a solicitor is the one instructing.
 * Everything else can be filled in afterwards from the enquiry itself.
 */
export function LogEnquiryModal({ open, onClose, onLogged, conversationId, prefill }: Props) {
  const [arrivalChannel, setArrivalChannel] = useState<IntakeArrivalChannel>('phone');
  const [instructingPartyType, setInstructingPartyType] =
    useState<IntakeInstructingPartyType>('professional_client');

  const [solicitorFirmName, setSolicitorFirmName] = useState('');
  const [solicitorName, setSolicitorName] = useState('');
  const [solicitorEmail, setSolicitorEmail] = useState('');
  const [solicitorPhone, setSolicitorPhone] = useState('');

  const [layClientName, setLayClientName] = useState('');
  const [layClientType, setLayClientType] = useState<'individual' | 'company'>('individual');
  const [opposingPartyName, setOpposingPartyName] = useState('');

  const [practiceArea, setPracticeArea] = useState('');
  const [courtName, setCourtName] = useState('');
  const [hearingDate, setHearingDate] = useState('');
  const [summary, setSummary] = useState('');
  const [urgency, setUrgency] = useState<IntakeUrgency>('routine');

  const [feeExpectation, setFeeExpectation] = useState('');
  const [quotedAmount, setQuotedAmount] = useState('');
  const [feeBasis, setFeeBasis] = useState<IntakeFeeBasis | ''>('');
  const [feeStatus, setFeeStatus] = useState<IntakeFeeStatus>('none');

  const [status, setStatus] = useState<'new' | 'reviewing' | 'assessing' | 'quoted'>('new');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const needsSolicitor = instructingPartyType === 'professional_client';

  // Shown WHILE typing, not only on submit — the complaint was a form that took "abcd" as an
  // email and said nothing. Both stay quiet until the field has something in it, so an
  // untouched optional field is never scolded. Empty-but-required is caught by validate().
  const liveEmailError = solicitorEmail.trim() && !isValidEmail(solicitorEmail) ? EMAIL_ERROR : '';
  const livePhoneError = solicitorPhone.trim() && !isValidPhone(solicitorPhone) ? PHONE_ERROR : '';
  // Names were checked on submit only, so "1212321321323" sat in Lay client looking accepted
  // until you pressed the button. Digits are legitimate in a company name ("3M Ltd"), so they
  // are not stripped as you type — the form says what is wrong instead.
  const liveClientError = entityNameError(layClientName);
  const liveFirmError = entityNameError(solicitorFirmName);
  const liveOpposingError = entityNameError(opposingPartyName);

  function reset() {
    setArrivalChannel('phone');
    setInstructingPartyType('professional_client');
    setSolicitorFirmName(''); setSolicitorName(''); setSolicitorEmail(''); setSolicitorPhone('');
    setLayClientName(''); setLayClientType('individual'); setOpposingPartyName('');
    setPracticeArea(''); setCourtName(''); setHearingDate(''); setSummary('');
    setUrgency('routine');
    setFeeExpectation(''); setQuotedAmount(''); setFeeBasis(''); setFeeStatus('none');
    setStatus('new');
    setErrors({});
  }

  // Seed from the conversation each time the modal opens. Deliberately a starting point and
  // nothing more: the sender's address and the subject are all an email reliably gives us,
  // and who the lay CLIENT is — the one field an enquiry cannot do without — is precisely
  // what an email does not say. A person supplies it before anything is created.
  useEffect(() => {
    if (!open) return;
    if (conversationId) setArrivalChannel('email');
    if (prefill?.solicitorEmail) setSolicitorEmail(prefill.solicitorEmail);
    if (prefill?.solicitorName) setSolicitorName(prefill.solicitorName);
    if (prefill?.summary) setSummary(prefill.summary);
    // Keyed on open + conversationId so reopening for a different conversation re-seeds.
  }, [open, conversationId, prefill?.solicitorEmail, prefill?.solicitorName, prefill?.summary]);

  const mutation = useMutation({
    mutationFn: (payload: LogEnquiryPayload) =>
      conversationId
        ? startEnquiryFromConversation(conversationId, payload)
        : logEnquiry(payload),
    onSuccess: (row) => {
      // Say what happened next, not just that it saved. A critical conflict found on
      // arrival is the single most important thing the clerk needs to know before they
      // promise anything to the person still on the phone.
      if (row.conflictStatus === 'critical') {
        toast.error('Enquiry logged — CRITICAL CONFLICT detected. Review before accepting.');
      } else if (row.conflictStatus === 'potential') {
        toast.warning('Enquiry logged — a possible conflict was found. Check before accepting.');
      } else {
        toast.success('Enquiry logged.');
      }
      reset();
      onLogged();
      onClose();
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  /**
   * Close and clear. The component stays mounted between openings, so without clearing here
   * a cancelled draft came back the next time the modal was opened — a clerk who abandoned
   * one enquiry would start the next one holding the previous caller's details, which is
   * exactly how the wrong client's name ends up on a record.
   */
  function handleClose() {
    reset();
    onClose();
  }

  if (!open) return null;

  function validate(): boolean {
    const e: Record<string, string> = {};

    if (!layClientName.trim()) e.layClientName = 'Who is the client?';
    else if (liveClientError) e.layClientName = liveClientError;

    if (summary.trim().length < 10) e.summary = 'Give at least a sentence about the enquiry.';

    // Optional, but if it was filled in it has to be usable.
    if (liveOpposingError) e.opposingPartyName = liveOpposingError;
    if (solicitorPhone.trim() && !isValidPhone(solicitorPhone)) e.solicitorPhone = PHONE_ERROR;

    if (needsSolicitor) {
      if (!solicitorFirmName.trim()) e.solicitorFirmName = 'Which firm is instructing?';
      else if (liveFirmError) e.solicitorFirmName = liveFirmError;

      if (!solicitorName.trim()) e.solicitorName = "The solicitor's name is required.";

      if (!solicitorEmail.trim()) e.solicitorEmail = "The solicitor's email is required.";
      else if (!isValidEmail(solicitorEmail)) e.solicitorEmail = EMAIL_ERROR;
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function submit() {
    if (!validate()) {
      toast.error('Please complete the highlighted fields.');
      return;
    }
    const payload: LogEnquiryPayload = {
      arrivalChannel,
      instructingPartyType,
      layClientName: layClientName.trim(),
      layClientType,
      summary: summary.trim(),
      urgency,
      feeStatus,
      status,
      ...(needsSolicitor && {
        solicitorFirmName: solicitorFirmName.trim(),
        solicitorName: solicitorName.trim(),
        solicitorEmail: solicitorEmail.trim(),
      }),
      ...(solicitorPhone.trim() && { solicitorPhone: solicitorPhone.trim() }),
      ...(opposingPartyName.trim() && { opposingPartyName: opposingPartyName.trim() }),
      ...(practiceArea.trim() && { practiceArea: practiceArea.trim() }),
      ...(courtName.trim() && { courtName: courtName.trim() }),
      ...(hearingDate && { hearingDate }),
      ...(feeExpectation.trim() && { feeExpectation: feeExpectation.trim() }),
      ...(quotedAmount.trim() && { quotedAmount: Number(quotedAmount) }),
      ...(feeBasis && { feeBasis }),
    };
    mutation.mutate(payload);
  }

  const partyHint = PARTY_TYPES.find((p) => p.value === instructingPartyType)?.hint;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto overflow-x-hidden bg-black/40 p-2 sm:p-8">
      <ScrollLock />
      <div className="w-full min-w-0 max-w-2xl rounded-lg border border-border bg-card shadow-lg">
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-6 sm:py-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">Log an enquiry</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Record work that came in by phone, email or referral. A conflict check runs
              straight away.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-[78vh] space-y-3 overflow-y-auto overflow-x-hidden px-4 py-4 sm:max-h-[70vh] sm:space-y-5 sm:px-6 sm:py-5">
          <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="enq-channel">How did it arrive?</label>
              <select
                id="enq-channel"
                className={inputCls}
                value={arrivalChannel}
                onChange={(e) => setArrivalChannel(e.target.value as IntakeArrivalChannel)}
              >
                {CHANNELS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="enq-party">Who is instructing?</label>
              <select
                id="enq-party"
                className={inputCls}
                value={instructingPartyType}
                onChange={(e) => setInstructingPartyType(e.target.value as IntakeInstructingPartyType)}
              >
                {PARTY_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
              {partyHint && <p className="mt-1 text-xs text-muted-foreground">{partyHint}</p>}
            </div>
          </div>

          {needsSolicitor && (
            <section className="space-y-3 rounded-md border border-border p-3 sm:space-y-4 sm:p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Instructing solicitor
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2">
                <Field label="Firm" required error={liveFirmError || errors.solicitorFirmName}>
                  <input className={inputCls} value={solicitorFirmName}
                    onChange={(e) => setSolicitorFirmName(e.target.value)}
                    placeholder="e.g. Marsden & Clarke LLP" />
                </Field>
                <Field label="Solicitor" required error={errors.solicitorName}>
                  {/* A person's name: anything that is not part of one is stripped as it is
                      typed, the same rule every other person-name field in the app uses. */}
                  <input className={inputCls} value={solicitorName}
                    onChange={(e) => setSolicitorName(sanitizeName(e.target.value))} placeholder="e.g. Jane Doe" />
                </Field>
                <Field label="Email" required error={liveEmailError || errors.solicitorEmail}>
                  <input className={inputCls} type="email" value={solicitorEmail}
                    onChange={(e) => setSolicitorEmail(e.target.value)} placeholder="jane@firm.co.uk" />
                </Field>
                <Field label="Phone">
                  {/* The country-aware input the rest of the app uses. Letters cannot be typed
                      into it at all, and a number of the wrong length for the chosen country
                      says so. The plain <input> it replaces accepted anything in silence. */}
                  <PhoneInput
                    value={solicitorPhone}
                    onChange={setSolicitorPhone}
                    error={livePhoneError || errors.solicitorPhone}
                  />
                </Field>
              </div>
            </section>
          )}

          <section className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2">
              <Field label="Lay client" required error={liveClientError || errors.layClientName}>
                <input className={inputCls} value={layClientName}
                  onChange={(e) => setLayClientName(e.target.value)} placeholder="e.g. Acme Ltd" />
              </Field>
              <Field label="Client type">
                <select className={inputCls} value={layClientType}
                  onChange={(e) => setLayClientType(e.target.value as 'individual' | 'company')}>
                  <option value="individual">Individual</option>
                  <option value="company">Company</option>
                </select>
              </Field>
              <Field
                label="Opposing party"
                hint="Recorded so the conflict check can run against both sides."
                error={liveOpposingError || errors.opposingPartyName}
              >
                <input className={inputCls} value={opposingPartyName}
                  onChange={(e) => setOpposingPartyName(e.target.value)} />
              </Field>
              <Field label="Practice area">
                <input className={inputCls} value={practiceArea}
                  onChange={(e) => setPracticeArea(e.target.value)} placeholder="e.g. Commercial" />
              </Field>
              <Field label="Court">
                <input className={inputCls} value={courtName}
                  onChange={(e) => setCourtName(e.target.value)} />
              </Field>
              <Field label="Hearing date" hint="If one is already listed.">
                <input className={`${inputCls} h-[34px] appearance-none sm:h-[38px]`} type="date" value={hearingDate}
                  onChange={(e) => setHearingDate(e.target.value)} />
              </Field>
            </div>

            <Field label="What is the enquiry?" required error={errors.summary}>
              <textarea className={`${inputCls} min-h-[80px]`} value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="e.g. Availability for a 3-day trial from 19 May. Contract dispute." />
            </Field>

            {/* "Counsel asked for" deliberately absent. The API accepts preferredBarristerId,
                but a disabled input that looks editable is a control that lies: a clerk
                taking a call tries to type the barrister's name into it and nothing happens.
                Counsel is named from the enquiry itself once it is open. */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <Field label="Urgency">
                <select className={inputCls} value={urgency}
                  onChange={(e) => setUrgency(e.target.value as IntakeUrgency)}>
                  {URGENCIES.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </select>
              </Field>
              <Field label="Log as">
                <select className={inputCls} value={status}
                  onChange={(e) => setStatus(e.target.value as typeof status)}>
                  {INITIAL_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
            </div>
          </section>

          <section className="space-y-3 rounded-md border border-border p-3 sm:space-y-4 sm:p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Fee position
            </h3>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <Field label="What they said" hint="Their words — e.g. &quot;~£25k brief fee&quot;." className="col-span-2 sm:col-span-1">
                <input className={inputCls} value={feeExpectation}
                  onChange={(e) => setFeeExpectation(e.target.value)} />
              </Field>
              <Field label="Fee position" className="col-span-2 sm:col-span-1">
                <select className={inputCls} value={feeStatus}
                  onChange={(e) => setFeeStatus(e.target.value as IntakeFeeStatus)}>
                  {FEE_STATUSES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </Field>
              <Field label="Amount quoted (£)">
                <input className={inputCls} type="number" min="0" value={quotedAmount}
                  onChange={(e) => setQuotedAmount(e.target.value)} />
              </Field>
              <Field label="Basis">
                <select className={inputCls} value={feeBasis}
                  onChange={(e) => setFeeBasis(e.target.value as IntakeFeeBasis | '')}>
                  <option value="">—</option>
                  {FEE_BASES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </Field>
            </div>
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
              <Warning className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              A fee recorded here is the position at enquiry. The agreed, contractual terms are
              recorded on the matter once the instruction is accepted.
            </p>
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3 sm:px-6 sm:py-4">
          <button
            onClick={handleClose}
            className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Log enquiry
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label, required, hint, error, children, className,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  /** Grid span, so a free-text field can hold the full line while short selects pair up. */
  className?: string;
}) {
  // The control is NESTED inside the <label>, which associates the two without needing a
  // generated id per field. Previously the label was a sibling, so 18 of the 20 fields had
  // no association at all: tapping the caption did nothing, and a screen reader announced
  // an unlabelled box — on a form whose whole job is recording who a client is.
  return (
    <div className={className}>
      <label className="block">
        <span className={labelCls}>
          {label} {required && <span className="text-red-500">*</span>}
        </span>
        {children}
      </label>
      {hint && !error && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}
